import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Header from '../components/Header';
import { Footer } from '../components/Footer';
import { useAuthStatus } from 'hooks/useAuthStatus';
import { useSubscriptionAccess } from 'hooks/useSubscriptionAccess';
import { PATHS } from 'routes/paths';
import { plansService } from 'services/plans/plansService';
import type { Plan as ApiPlan } from 'services/plans/plansService';

type PricingPlan = {
  id: number;
  title: string;
  priceLabel: string;
  purchasePrice: number;
  purchasePlanName: string;
  features: string[];
  highlighted: boolean;
};

const staticPlans: PricingPlan[] = [
  {
    id: 1,
    title: 'Basic',
    priceLabel: '29',
    purchasePrice: 299,
    purchasePlanName: 'Basic Monthly',
    features: ['Access to Basic Courses', 'Access on any device', 'Cancel Anytime'],
    highlighted: false,
  },
  {
    id: 2,
    title: 'Premium',
    priceLabel: '99',
    purchasePrice: 499,
    purchasePlanName: 'Pro Monthly',
    features: ['Access to All Courses', 'Access On Any Device', 'Unlimited Downloads', 'Unlimited Transcripts', 'Cancel Anytime'],
    highlighted: true,
  },
  {
    id: 3,
    title: 'Standard',
    priceLabel: '59',
    purchasePrice: 399,
    purchasePlanName: 'Standard Monthly',
    features: ['Access to Advanced Courses', 'Access on any device', 'Limited Downloads', 'Limited Transcripts', 'Cancel Anytime'],
    highlighted: false,
  },
];

const getStaticPlanTemplate = (name = "") => {
  const normalizedName = name.toLowerCase();
  return staticPlans.find((plan) => normalizedName.includes(plan.title.toLowerCase()));
};

const getPlanDisplayOrder = (plan: PricingPlan) => {
  const normalizedName = `${plan.title} ${plan.purchasePlanName}`.toLowerCase();

  if (normalizedName.includes('basic')) return 1;
  if (normalizedName.includes('premium') || normalizedName.includes('pro')) return 2;
  if (normalizedName.includes('standard')) return 3;

  return 99;
};

const sortPlansForDisplay = (plans: PricingPlan[]) => {
  return [...plans].sort((firstPlan, secondPlan) => {
    const firstOrder = getPlanDisplayOrder(firstPlan);
    const secondOrder = getPlanDisplayOrder(secondPlan);

    if (firstOrder !== secondOrder) return firstOrder - secondOrder;

    return firstPlan.purchasePrice - secondPlan.purchasePrice;
  });
};

const fallbackPlans = sortPlansForDisplay(staticPlans);

const getPlanTitle = (name: string) => name.replace(/\s+monthly$/i, '').trim() || name;

const getPlanPriceLabel = (price: number) => {
  return Number.isInteger(price) ? String(price) : price.toFixed(2);
};

const parsePlanDescription = (description: string | null) => {
  if (!description) return [];

  return description
    .split(/\r?\n|,/)
    .map((feature) => feature.trim())
    .filter(Boolean);
};

const mapApiPlanToPricingPlan = (plan: ApiPlan): PricingPlan | null => {
  const price = Number(plan.price);

  if (!Number.isFinite(price)) return null;

  const template = getStaticPlanTemplate(plan.name);

  return {
    id: plan.id,
    title: getPlanTitle(plan.name),
    priceLabel: getPlanPriceLabel(price),
    purchasePrice: price,
    purchasePlanName: plan.name,
    features: parsePlanDescription(plan.description).length
      ? parsePlanDescription(plan.description)
      : template?.features ?? ['Access on any device', 'Cancel Anytime'],
    highlighted: template?.highlighted ?? false,
  };
};

export default function PricingPage() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();
  const hasSubscriptionAccess = useSubscriptionAccess();
  const [apiPlans, setApiPlans] = useState<PricingPlan[]>([]);
  const [plansError, setPlansError] = useState('');
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);

  useEffect(() => {
    if (isAuthenticated && hasSubscriptionAccess) {
      navigate(PATHS.library, { replace: true });
    }
  }, [hasSubscriptionAccess, isAuthenticated, navigate]);

  useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      try {
        const response = await plansService.getPlans();
        const activePlans = sortPlansForDisplay(response.data
          .filter((plan) => plan.is_active)
          .map(mapApiPlanToPricingPlan)
          .filter((plan): plan is PricingPlan => Boolean(plan)));

        if (!isMounted) return;

        setApiPlans(activePlans.length ? activePlans : fallbackPlans);
        setPlansError('');
      } catch (error) {
        if (!isMounted) return;
        setApiPlans(fallbackPlans);
        setPlansError('');
      } finally {
        if (isMounted) {
          setIsLoadingPlans(false);
        }
      }
    };

    void loadPlans();

    return () => {
      isMounted = false;
    };
  }, []);

  const plans = useMemo(() => apiPlans, [apiPlans]);

  const stripedBg = {
    backgroundImage: 'repeating-linear-gradient(-45deg, var(--bg-stripe), var(--bg-stripe) 2px, var(--bg-body) 2px, var(--bg-body) 10px)',
    backgroundColor: 'var(--bg-body)'
  };

  type CheckmarkProps = { highlighted?: boolean };
  const Checkmark: React.FC<CheckmarkProps> = ({ highlighted = false }) => (
    <svg
      className={`w-5 h-5 flex-shrink-0 ${highlighted ? 'text-[#25D366]' : 'text-[var(--text-900)]'}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
    </svg>
  );

  const handleGetStarted = (plan: PricingPlan) => {
    navigate(PATHS.checkout, {
      state: {
        plan: {
          id: plan.id,
          planId: plan.id,
          title: plan.title,
          name: plan.purchasePlanName,
          priceLabel: plan.priceLabel,
          price: plan.purchasePrice,
        },
      },
    });
  };

  return (
    <div style={stripedBg} className="min-h-screen w-full py-2 px-2 sm:py-8 sm:px-4 md:px-8 lg:px-12 flex justify-center font-sans text-[var(--text-900)] overflow-hidden">
      <div
        className="w-full bg-[var(--bg-box)] rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden border border-[var(--border-200)] flex flex-col relative"
        style={{ boxShadow: 'var(--shadow-box)' }}
      >
        <Header />

        <main className="flex-grow pb-20 sm:pb-28 md:pb-32">
          <section className="pt-20 sm:pt-28 md:pt-32 pb-10 sm:pb-16 md:pb-20 px-2 sm:px-4 md:px-8 text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-[var(--text-900)] mb-6 tracking-tight">
              Simple, transparent pricing
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-[var(--text-500)] leading-relaxed font-medium">
              Choose the plan that best fits your learning goals. Upgrade, downgrade, or cancel anytime.
            </p>
          </section>

          <section className="px-2 sm:px-4 md:px-8 max-w-6xl mx-auto">
            {isLoadingPlans ? (
              <div className="py-20 text-center">
                <p className="text-base font-semibold text-[var(--text-500)]">Loading plans...</p>
              </div>
            ) : plansError ? (
              <div className="py-20 text-center">
                <p className="text-base font-semibold text-[var(--text-500)]">{plansError}</p>
              </div>
            ) : null}
            {!isLoadingPlans && !plansError ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center">
              {plans.map((plan) => {
                return (
                  <div
                    key={plan.id}
                    className={plan.highlighted ? 'bg-[var(--primary)] rounded-[2rem] p-10 flex flex-col h-full md:scale-105 relative z-10' : 'bg-[var(--bg-white)] border border-[var(--border-200)] rounded-[2rem] p-10 flex flex-col h-full'}
                    style={{ boxShadow: plan.highlighted ? 'var(--shadow-box)' : 'var(--shadow-sm)' }}
                  >
                    {plan.highlighted ? (
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-gradient-to-r from-transparent via-[#25D366] to-transparent opacity-50 rounded-full"></div>
                    ) : null}

                    <div className={`text-center ${plan.highlighted ? 'border-b border-[var(--text-700)]' : 'border-b border-[var(--border-100)]'} pb-8 mb-8`}>
                      <h2 className={`text-2xl font-bold mb-4 ${plan.highlighted ? 'text-white' : 'text-[var(--text-900)]'}`}>{plan.title}</h2>
                      <div className={`flex items-baseline justify-center gap-1 ${plan.highlighted ? 'text-white' : ''}`}>
                        <span className={`font-extrabold tracking-tight ${plan.highlighted ? 'text-6xl' : 'text-5xl text-[var(--text-900)]'}`}>{plan.priceLabel}</span>
                        <span className={`font-medium ${plan.highlighted ? 'text-white' : 'text-[var(--text-500)]'}`}>/Per Month</span>
                      </div>
                    </div>

                    <ul className="space-y-6 mb-10 flex-grow">
                      {plan.features.map((feature) => (
                        <li key={feature} className={`flex items-center gap-4 font-medium ${plan.highlighted ? 'text-white' : 'text-[var(--text-600)]'}`}>
                          <Checkmark highlighted={plan.highlighted} /> {feature}
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => void handleGetStarted(plan)}
                      className={plan.highlighted ? 'bg-[var(--bg-white)] text-[var(--text-900)] px-12 py-5 rounded-2xl font-bold text-lg hover:bg-[var(--surface-100)] transition-all hover:-translate-y-1 shadow-xl relative z-10' : 'w-full bg-[var(--surface-100)] text-[var(--text-900)] border border-[var(--border-200)] px-6 py-4 rounded-xl font-bold hover:bg-[var(--surface-200)] transition-colors mt-auto'}
                    >
                      BUY NOW
                    </button>
                  </div>
                );
              })}
            </div>
            ) : null}
          </section>
        </main>

        <Footer />
      </div>
    </div>
  );
}
